import { useCallback, useEffect, useState } from 'react';
import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  getFirstCollision,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensors,
  useSensor,
  Active,
  Over,
  DragEndEvent,
  DragOverEvent,
  PointerSensor,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, SortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@chakra-ui/react';
import { useToast, useVariable } from 'hooks';
import { useConfirm } from 'contexts';

import AddCategory from '../AddCategory/AddCategory';
import SortableItem from '../SortableItem/SortableItem';
import DroppableContainer from '../DroppableContainer';

type SortableContainerSubItemsMap = Record<UniqueIdentifier, UniqueIdentifier[]>;
export type SortableContainerSubItems = [UniqueIdentifier, UniqueIdentifier[]];
export type SortableContainerSubItemsWithPrimary =
  | [UniqueIdentifier, UniqueIdentifier[], UniqueIdentifier]
  | [UniqueIdentifier, UniqueIdentifier[]];

type SortableContainerListProps = {
  columns?: number;
  strategy?: SortingStrategy;
  scrollable?: boolean;
  primary?: UniqueIdentifier;
  containers: SortableContainerSubItems[];
  items: Record<string, React.ReactNode>;
  onUpdateContainerItems(data: any): void;
  onUpdatePrimary(newId: UniqueIdentifier): void;
};

const SortableContainerList = ({
  columns,
  strategy,
  scrollable,
  primary,
  containers: initialContainers,
  items,
  onUpdateContainerItems,
  onUpdatePrimary,
}: SortableContainerListProps) => {
  const initialContainerSubItems = initialContainers ? Object.fromEntries(initialContainers) : {};

  const [containers, setContainers] = useState<UniqueIdentifier[]>(Object.keys(initialContainerSubItems));
  const [activeId, setActiveId] = useState<UniqueIdentifier>();
  const [showForm, setShowForm] = useState(false);
  const [prevOverId, setPrevOverId] = useVariable<UniqueIdentifier>();
  const [showConfirm] = useConfirm();
  const [showToast] = useToast();
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleUpdateContainerSubItems = (updatedItems: Record<string, UniqueIdentifier[]>) => {
    const orderedItems = Object.entries(updatedItems)
      .sort(([containerId1], [containerId2]) => containers.indexOf(containerId1) - containers.indexOf(containerId2))
      .reduce((acc, [containerId, subItems]) => {
        const primaryArr = containerId === primary ? ['primary'] : [];

        return [...acc, [containerId, subItems, ...primaryArr]] as SortableContainerSubItemsWithPrimary[];
      }, [] as SortableContainerSubItemsWithPrimary[]);

    onUpdateContainerItems(orderedItems);
  };

  useEffect(() => {
    handleUpdateContainerSubItems(initialContainerSubItems);
  }, [containers]);

  const containerSubItemNodeMap = Object.entries(initialContainerSubItems).reduce(
    (acc, [containerId, subItems]) => ({
      ...acc,
      [containerId]: subItems.reduce((acc2, val) => ({ ...acc2, [val]: items[val] }), {}),
    }),
    {} as Record<UniqueIdentifier, Record<UniqueIdentifier, React.ReactNode>>
  );

  const findContainer = (containerId: UniqueIdentifier) => {
    // It's a container
    if (containerId in containerSubItemNodeMap) {
      return containerId;
    }

    const subItemContainerId = Object.keys(initialContainerSubItems).find((containerId2) =>
      initialContainerSubItems[containerId2].includes(containerId)
    );

    return subItemContainerId;
  };

  const collisionDetectionStrategy = useCallback<CollisionDetection>(
    (args) => {
      if (activeId && activeId in initialContainerSubItems) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in initialContainerSubItems),
        });
      }

      const pointerIntersections = pointerWithin(args);

      // If there are droppables intersecting with the pointer, return those
      const intersections = pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args);
      const firstCollisionId = getFirstCollision(intersections, 'id');

      const containerItems =
        firstCollisionId !== null && initialContainerSubItems[firstCollisionId!]
          ? initialContainerSubItems[firstCollisionId!]
          : [];

      // Return the closest droppable within that container
      const closestCenterId =
        containerItems.length > 0
          ? closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) => container.id !== firstCollisionId && containerItems.includes(container.id)
              ),
            })[0]?.id
          : firstCollisionId;

      // Fallback to first collision or previous drag over
      const id = closestCenterId ?? firstCollisionId ?? prevOverId;

      setPrevOverId(id);

      return [{ id }];
    },
    [activeId, initialContainerSubItems]
  );

  const handleAdd = (newContainerId: UniqueIdentifier) => {
    setContainers((prevContainers) => [...prevContainers, newContainerId]);

    const updatedItems = {
      ...initialContainerSubItems,
      [newContainerId]: [],
    };

    handleUpdateContainerSubItems(updatedItems);
  };

  const handleRename = (oldContainerId: UniqueIdentifier, newContainerId: UniqueIdentifier) => {
    setContainers((prevContainers) => prevContainers.map((item) => (item === oldContainerId ? newContainerId : item)));

    const updatedItems = Object.entries(initialContainerSubItems).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key === oldContainerId ? newContainerId : key]: value,
      }),
      {}
    );

    handleUpdateContainerSubItems(updatedItems);

    showToast({
      message: `${oldContainerId} renamed successfully.`,
    });

    if (oldContainerId !== primary) return;

    onUpdatePrimary(newContainerId);
  };

  const renderAddCategory = showForm ? (
    <AddCategory closePopup={() => setShowForm(false)} onUpdate={handleAdd} containers={containers} />
  ) : null;

  const handleRemove = (containerId: UniqueIdentifier) => {
    const deletedContainerItems = initialContainerSubItems[containerId];

    // Move container items to primary container group
    const updatedItems = Object.entries(initialContainerSubItems).reduce((acc, [key, value]) => {
      if (key === containerId) return acc;

      return {
        ...acc,
        [key]: key === primary ? [...value, ...(deletedContainerItems ?? [])] : value,
      };
    }, {});

    handleUpdateContainerSubItems(updatedItems);
    setContainers((prevContainers) => prevContainers.filter((id) => id !== containerId));

    showToast({
      message: `${containerId} deleted successfully.`,
    });
  };

  const handleRemovePrompt = (containerId: UniqueIdentifier) => {
    if (containerId === primary) {
      showToast({
        status: 'error',
        message: 'The primary parameter group cannot be deleted.',
      });
      return;
    }

    if (!primary) return;

    showConfirm({
      onConfirm: () => handleRemove(containerId),
      title: 'Delete Parameter Group',
      message: `Are you sure you want to delete the "${containerId}" parameter group?`,
    });
  };

  const handleAddColumn = () => setShowForm(true);

  const handleCalculateDragIndex = (
    prevItems: SortableContainerSubItemsMap,
    active: Active,
    over: Over | null,
    overId: UniqueIdentifier,
    overContainer: UniqueIdentifier
  ) => {
    const overItems = initialContainerSubItems[overContainer];

    const overIndex = overItems.indexOf(overId);

    if (overId in prevItems) {
      return overItems.length + 1;
    }

    const isBelowOverItem =
      over && active.rect.current.translated && active.rect.current.translated.top > over.rect.top + over.rect.height;

    const modifier = isBelowOverItem ? 1 : 0;

    return overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    const overContainer = over?.id ? findContainer(over?.id) : null;
    const activeContainer = !(active.id in containerSubItemNodeMap) ? findContainer(active.id) : null;

    if (!over?.id || !overContainer || !activeContainer || activeContainer === overContainer) {
      return;
    }
    const activeSubItem = initialContainerSubItems[activeContainer].indexOf(active.id);

    const newIndex = handleCalculateDragIndex(initialContainerSubItems, active, over, over?.id, overContainer);

    const updatedItems = {
      ...initialContainerSubItems,
      [activeContainer]: initialContainerSubItems[activeContainer].filter((item) => item !== active.id),
      [overContainer]: [
        ...initialContainerSubItems[overContainer].slice(0, newIndex),
        initialContainerSubItems[activeContainer][activeSubItem],
        ...initialContainerSubItems[overContainer].slice(newIndex, initialContainerSubItems[overContainer].length),
      ],
    };

    handleUpdateContainerSubItems(updatedItems);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id in initialContainerSubItems && over?.id) {
      setContainers((prevContainers) => {
        const activeIndex = prevContainers.indexOf(active.id);
        const overIndex = prevContainers.indexOf(over.id);

        return arrayMove(prevContainers, activeIndex, overIndex);
      });
    }

    const activeContainer = findContainer(active.id);
    const overContainer = over?.id ? findContainer(over?.id) : undefined;

    setActiveId(undefined);

    if (!activeContainer || !over?.id || !overContainer || active.id in initialContainerSubItems) {
      return;
    }

    // It's a sub item

    const activeIndex = initialContainerSubItems[activeContainer].indexOf(active.id);
    const overIndex = initialContainerSubItems[overContainer].indexOf(over.id);

    if (activeIndex === -1 || activeIndex === overIndex) return;

    const updatedItems = {
      ...initialContainerSubItems,
      [overContainer]: arrayMove(initialContainerSubItems[overContainer], activeIndex, overIndex),
    };

    handleUpdateContainerSubItems(updatedItems);
  };

  const renderContainers = containers
    .filter((container) => container in initialContainerSubItems)
    .map((containerId) => (
      <DroppableContainer
        key={containerId}
        id={containerId}
        label={`${containerId}`}
        columns={columns}
        items={initialContainerSubItems[containerId]}
        scrollable={scrollable}
        onRemove={() => handleRemovePrompt(containerId)}
        categoryEdit={handleRename}
        primary={containerId === primary}
      >
        <SortableContext
          // Items in the correct order so the values from the container indexes map sorted by key
          items={initialContainerSubItems[containerId]}
          strategy={strategy}
        >
          {initialContainerSubItems[containerId].map((value) => (
            <SortableItem
              key={`${containerId}-${value}`}
              id={value}
              item={containerSubItemNodeMap[containerId][value]}
            />
          ))}
        </SortableContext>
      </DroppableContainer>
    ));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={containers} strategy={verticalListSortingStrategy}>
        {renderContainers}
      </SortableContext>
      <Button onClick={handleAddColumn} w="fit-content">
        Add Category
      </Button>
      {renderAddCategory}
    </DndContext>
  );
};

export default SortableContainerList;
